export type CollaboratorRole = "collaborator" | "builder";

export interface CollaboratorAccess {
  role: CollaboratorRole;
  viewOnly: boolean;
}

export interface CollaboratorItem {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: CollaboratorRole | null;
  viewOnly?: boolean;
  user_type?: string;
  isExisting?: boolean;
  profile_asset?: string | null;
}

export interface CollaboratorInvite {
  email: string;
  display_name: string;
  phone?: string;
  view_only?: boolean;
}

export interface BuilderInvite {
  email: string;
  display_name: string;
  phone?: string;
}

// view_only is a collaborator membership setting; a builder is a builder.
export const deriveCollaboratorAccess = (membership: {
  user_type?: string;
  view_only?: boolean;
}): CollaboratorAccess => {
  const role: CollaboratorRole =
    membership.user_type === "builder" ? "builder" : "collaborator";
  return {
    role,
    viewOnly: role === "collaborator" && membership.view_only === true,
  };
};

const trimmedPhone = (phone?: string) => {
  const value = (phone || "").trim();
  return value.length > 0 ? value : undefined;
};

export const splitCollaboratorInvites = (items: CollaboratorItem[]) => {
  const collaboratorEmails: CollaboratorInvite[] = [];
  const builderEmails: BuilderInvite[] = [];

  items.forEach((item) => {
    if (item.isExisting) return;

    const phone = trimmedPhone(item.phone);
    const base = {
      email: item.email || "",
      display_name: item.name || "",
      ...(phone ? { phone } : {}),
    };

    if (item.role === "builder") {
      builderEmails.push(base);
      return;
    }

    collaboratorEmails.push({ ...base, view_only: item.viewOnly === true });
  });

  return { collaboratorEmails, builderEmails };
};

export const existingViewOnlyIds = (
  items: CollaboratorItem[],
  selectedUserIds: number[],
): number[] =>
  items
    .filter(
      (item) =>
        item.isExisting &&
        item.role === "collaborator" &&
        item.viewOnly === true &&
        String(item.id).startsWith("existing-"),
    )
    .map((item) => Number(String(item.id).replace("existing-", "")))
    .filter((userId) => !isNaN(userId) && selectedUserIds.includes(userId));

export const appendCollaboratorInvites = (
  formData: FormData,
  items: CollaboratorItem[],
) => {
  const { collaboratorEmails, builderEmails } = splitCollaboratorInvites(items);

  collaboratorEmails.forEach((entry, index) => {
    formData.append(`collaborator_emails[${index}][email]`, entry.email);
    formData.append(
      `collaborator_emails[${index}][display_name]`,
      entry.display_name,
    );
    if (entry.phone) {
      formData.append(`collaborator_emails[${index}][phone]`, entry.phone);
    }
    formData.append(
      `collaborator_emails[${index}][view_only]`,
      String(entry.view_only === true),
    );
  });

  builderEmails.forEach((entry, index) => {
    formData.append(`builder_emails[${index}][email]`, entry.email);
    formData.append(
      `builder_emails[${index}][display_name]`,
      entry.display_name,
    );
    if (entry.phone) {
      formData.append(`builder_emails[${index}][phone]`, entry.phone);
    }
  });
};
